/**
 * F-GTCC-COCKPIT-EXEC1 — Monitoring Cockpit, the OBSERVABILITY default view,
 * read-only.
 *
 * 1:1 rebuild of the six-tab cockpit shell per `GTCC_REFERENCE_2026-09-01.html`
 * / `GTCC_1ZU1_PROGRAM.md`: Executive Summary / Infrastructure & VPS Health /
 * Services & Dependencies / Rules & Telemetry / Delivery & Watchdog /
 * Historical Trends (tab keys `exec`/`infra`/`svc`/`rules`/`delivery`/`trends`).
 *
 * This container owns the tab-bar shell and the "exec" tab content. `infra`
 * renders C-GTCC-COCKPIT-INFRA1's `InfraTab` (merged, wired in here since
 * this lane owns the shell). `svc` and `delivery` render lane K's `SvcTab`
 * and `DeliveryTab`, `rules` renders lane L's `RulesTab`, `trends` renders
 * lane J's `TrendsTab` — all merged.
 *
 * The active tab lives in the URL as `?tab=<id>`, read exactly the way
 * `GtccShell.tsx` reads `?gtcc=<viewKey>`.
 *
 * Tabs are the design-system `Tabs` primitive (roving tabindex, arrow keys,
 * Home / End, RTL-aware).
 *
 * Read-only: no write endpoint, no mutation, not even a disabled control.
 */
import { Stack, Tabs, type TabItem } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";

import { fetchGtccExecSummary } from "../../exec-api.js";
import type { GtccExecSummaryDto } from "../../exec-types.js";
import { FreshBadge } from "./components/ExecPanels.js";
import { EXEC_TAB_REFETCH_MS, EXEC_TAB_STALE_MS, ExecTab } from "./ExecTab.js";
import { InfraTab } from "./tabs/InfraTab.js";
import { DeliveryTab } from "./tabs/DeliveryTab.js";
import { RulesTab } from "./tabs/RulesTab.js";
import { SvcTab } from "./tabs/SvcTab.js";
import { TrendsTab } from "./tabs/TrendsTab.js";

/* ------------------------------------------------------------------ tabs */

const COCKPIT_TAB_IDS = ["exec", "infra", "svc", "rules", "delivery", "trends"] as const;
type CockpitTabId = (typeof COCKPIT_TAB_IDS)[number];
const DEFAULT_COCKPIT_TAB: CockpitTabId = "exec";
const TAB_ID_PREFIX = "gtcc-cockpit";

function isCockpitTabId(value: string): value is CockpitTabId {
  return (COCKPIT_TAB_IDS as ReadonlyArray<string>).includes(value);
}

/** Mirrors `initialViewKeyFromQuery()` in `GtccShell.tsx`. */
function initialTabFromQuery(): CockpitTabId {
  if (typeof window === "undefined") return DEFAULT_COCKPIT_TAB;
  const requested = new URLSearchParams(window.location.search).get("tab");
  if (requested !== null && isCockpitTabId(requested)) return requested;
  return DEFAULT_COCKPIT_TAB;
}

function writeTabToQuery(next: CockpitTabId): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set("tab", next);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

const HEAD_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "var(--gt-sys-space-3, 12px)",
  flexWrap: "wrap",
};

/*
 * B-GTCC-COCKPIT-5PX1 — Referenz-`h1` ist 19px/700 Sora
 * (`--gt-cmp-gtcc-view-title-size`, tokens.css), nicht `font-size-2xl` (24px).
 * Die selbst erfundene Groesse war genau die 5px zu viel, die die
 * Inhaltsbox der sechs Cockpit-Reiter tiefer begannen liess als die Referenz.
 */
const HEADING_STYLE: CSSProperties = {
  margin: 0,
  fontFamily: "var(--gt-cmp-gtcc-view-title-family)",
  fontSize: "var(--gt-cmp-gtcc-view-title-size)",
  fontWeight: "var(--gt-cmp-gtcc-view-title-weight)",
  color: "var(--gt-sys-text)",
};

const MUTED_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text-muted)",
};

/* ------------------------------------------------------------------ view */

export function GtccCockpitView(): ReactNode {
  const { t, direction } = useLocale();
  const [tab, setTab] = useState<CockpitTabId>(initialTabFromQuery);

  /*
   * Same query key/cache window as `ExecTab.tsx`'s own exec-summary query:
   * whichever mounts first fetches, the other reads the shared cache entry.
   * The head's Fresh-Badge only appears once real freshness/confidence data
   * has arrived — never a fabricated "fresh" claim before the first fetch.
   */
  const summaryQuery = useQuery({
    queryKey: ["gtcc", "cockpit", "exec-summary"],
    queryFn: ({ signal }) => fetchGtccExecSummary(signal),
    staleTime: EXEC_TAB_STALE_MS,
    refetchInterval: EXEC_TAB_REFETCH_MS,
  });
  const summary: GtccExecSummaryDto | null =
    summaryQuery.data !== undefined && summaryQuery.data.ok ? summaryQuery.data.data : null;
  const staleSourceCount = summary?.dataConfidence?.staleSources?.length ?? 0;

  // D-GTCC-FRAME1 — the Shell's breadcrumb reads the active tab from `?tab=`
  // (GtccShell.tsx) to show it as the 3rd segment; without the query param a
  // fresh load (no `?tab=` yet) would leave the breadcrumb one level short
  // even though a tab is in fact active. Writing the resolved id once on
  // mount keeps the URL truthful without changing any rendered content.
  useEffect(() => {
    writeTabToQuery(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTabChange = useCallback((next: string) => {
    if (!isCockpitTabId(next)) return;
    setTab(next);
    writeTabToQuery(next);
  }, []);

  const items: ReadonlyArray<TabItem> = COCKPIT_TAB_IDS.map((id) => ({
    id,
    label: t(`admin.gtcc.cockpit.tab.${id}`),
    testId: `gtcc-cockpit-tab-${id}`,
  }));

  /*
   * A-GTCC-POLSTERUNG-24PX1 — kein eigenes Polster auf dem Wurzelknoten. Den
   * Rahmen setzt die Shell (`GtccShell.tsx`: `18px 22px 30px`, identisch zur
   * Referenz `GTCC_REFERENCE_2026-09-01.html:62`); ein `padding` hier polsterte
   * ein zweites Mal und verschob die sechs Reiter gemessen um 24 px nach innen.
   */
  return (
    <div
      data-testid="gtcc-view-cockpit"
      style={{
        display: "grid",
        gap: "var(--gt-sys-space-4, 16px)",
      }}
    >
      <Stack gap={0}>
        <div style={HEAD_STYLE}>
          <h2 style={HEADING_STYLE}>{t("admin.gtcc.cockpit.title")}</h2>
          {summary !== null ? (
            <FreshBadge
              testId="gtcc-cockpit-head-fresh-badge"
              freshnessSeconds={summary.freshnessSeconds}
              stale={staleSourceCount > 0}
              label={`${t("admin.gtcc.cockpit.exec.fresh_ago", { seconds: summary.freshnessSeconds })} · ${
                staleSourceCount > 0
                  ? t("admin.gtcc.cockpit.header.incomplete")
                  : t("admin.gtcc.cockpit.header.complete")
              }`}
            />
          ) : null}
        </div>
        <p style={MUTED_STYLE}>{t("admin.gtcc.cockpit.subtitle")}</p>
      </Stack>

      <Tabs
        items={items}
        value={tab}
        onChange={onTabChange}
        ariaLabel={t("admin.gtcc.cockpit.tabs_aria")}
        idPrefix={TAB_ID_PREFIX}
        direction={direction}
        variant="underline"
        dataTestId="gtcc-cockpit-tablist"
      />

      <div
        role="tabpanel"
        id={`${TAB_ID_PREFIX}-panel-${tab}`}
        aria-labelledby={`${TAB_ID_PREFIX}-tab-${tab}`}
        data-testid={`gtcc-cockpit-panel-${tab}`}
      >
        {tab === "exec" ? <ExecTab /> : null}
        {tab === "infra" ? <InfraTab direction={direction} /> : null}
        {tab === "svc" ? <SvcTab /> : null}
        {tab === "rules" ? <RulesTab /> : null}
        {tab === "delivery" ? <DeliveryTab /> : null}
        {tab === "trends" ? <TrendsTab /> : null}
      </div>
    </div>
  );
}
